import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  ContentChild,
  ElementRef
} from '@angular/core';

/**
 * Segmented Item Component
 *
 * Individual segment within an hc-segmented container.
 * Matches Figma Navigation/Segmented/Item (node 5544-27028).
 *
 * Figma property → Angular mapping:
 *   - contLabel → projected <ng-content> (label text)
 *   - showIcon  → auto-detected via @ContentChild('[segmentIcon]')
 *   - State     → managed by parent hc-segmented via `isActive` property
 *
 * @example
 * ```html
 * <hc-segmented-item value="room">Per room</hc-segmented-item>
 *
 * <hc-segmented-item value="overview">
 *   <lucide-icon segmentIcon name="grid" class="icon-sm"></lucide-icon>
 *   Overview
 * </hc-segmented-item>
 * ```
 */
@Component({
  selector: 'hc-segmented-item',
  standalone: false,
  templateUrl: './segmented-item.component.html',
  styleUrl: './segmented-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SegmentedItemComponent {
  constructor(public cdr: ChangeDetectorRef) {}

  /** Unique value identifying this segment (required) */
  @Input() value = '';

  /** Whether this individual item is disabled */
  @Input() disabled = false;

  /** Detect projected icon content */
  @ContentChild('segmentIcon') iconContent!: ElementRef;

  /** Set by parent SegmentedComponent — do not bind directly */
  isActive = false;

  /** Set by parent when entire group is disabled */
  groupDisabled = false;

  /** Whether this item is effectively disabled (own or group) */
  get isDisabled(): boolean {
    return this.disabled || this.groupDisabled;
  }

  get itemClass(): string {
    const classes = ['hc-segmented__item'];
    if (this.isActive) classes.push('hc-segmented__item--active');
    if (this.isDisabled) classes.push('hc-segmented__item--disabled');
    return classes.join(' ');
  }
}
